"use client"

import { useState, useEffect } from "react"
import { SiteService, type MapData, type CreateMapRequest } from "@/services/SiteService"

export function useMaps() {
  const [maps, setMaps] = useState<MapData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchMaps = async () => {
    try {
      setLoading(true)
      setError(null)
      const userMaps = await SiteService.getUserMaps()
      setMaps(userMaps)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch maps")
    } finally {
      setLoading(false)
    }
  }

  const createMap = async (data: CreateMapRequest): Promise<MapData | null> => {
    try {
      setError(null)
      const newMap = await SiteService.createMap(data)
      setMaps((prev) => [newMap, ...prev])
      return newMap
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create map")
      return null
    }
  }

  const deleteMap = async (mapId: string): Promise<boolean> => {
    try {
      setError(null)
      await SiteService.deleteMap(mapId)
      setMaps((prev) => prev.filter((map) => map.id !== mapId))
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete map")
      return false
    }
  }

  const duplicateMap = async (mapId: string, name: string): Promise<MapData | null> => {
    try {
      setError(null)
      const duplicatedMap = await SiteService.duplicateMap(mapId, name)
      setMaps((prev) => [duplicatedMap, ...prev])
      return duplicatedMap
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to duplicate map")
      return null
    }
  }

  useEffect(() => {
    fetchMaps()
  }, [])

  return {
    maps,
    loading,
    error,
    fetchMaps,
    createMap,
    deleteMap,
    duplicateMap,
  }
}
